import { DOCUMENT } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  GuardsCheckEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval, Subject, Subscription, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GlobalLoadingSpinnerComponent } from './core/components/global-loading-spinner/global-loading-spinner.component';
import { ServiceUnavailableComponent } from './core/components/service-unavailable/service-unavailable.component';
import { BrandService } from './core/services/brand.service';
import { ShellChromeService } from './core/services/shell-chrome.service';
import { GlobalLoadingFacadeService } from './core/state/global-loading/global-loading-facade.service';
import { SpinnerMessageService } from './shared/services/spinner-message.service';
import { PullToRefreshComponent } from './shared/components/pull-to-refresh/pull-to-refresh.component';
import { OrderUIFacadeService } from './salesrep/state/order-ui/order-ui-facade.service';
import { TranslocoService } from '@jsverse/transloco';
import { loadingMessageKeyForRoute } from './app-loading-message';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  imports: [GlobalLoadingSpinnerComponent, RouterOutlet, PullToRefreshComponent, ServiceUnavailableComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private brandService = inject(BrandService);
  private globalLoading = inject(GlobalLoadingFacadeService);
  private swUpdate = inject(SwUpdate);
  spinnerMsg = inject(SpinnerMessageService);
  private snackBar = inject(MatSnackBar);
  private doc = inject<Document>(DOCUMENT);
  private orderUI = inject(OrderUIFacadeService);
  private transloco = inject(TranslocoService);
  // Created at the root so route mobile-chrome opt-ins also apply outside the shell.
  private readonly shellChrome = inject(ShellChromeService);

  /**
   * True when `BrandService` could not resolve a brand for this host. Settled
   * before first render — `initializeBrand` runs as an APP_INITIALIZER — and
   * gates the entire template, so an unconfigured domain never reaches the
   * router (see `app.component.html`).
   */
  readonly serviceUnavailable = this.brandService.serviceUnavailable;

  private destroy$ = new Subject<void>();
  private navigationTimeout?: Subscription;

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    // Tag body with environment class for theming
    try {
      const envName = (environment as any)?.env || (environment as any)?.envName || 'stage';
      const cls = `env-${envName}`;
      this.doc?.body?.classList?.add(cls);
      console.log(`[AppComponent] Applied environment class: ${cls}`);
    } catch {
      // Environment class tagging is cosmetic (theming only) — never block app init on it.
    }

    // Initialize connection quality monitoring
    console.log('🌐 [AppComponent] Initializing connection quality monitoring');
    // The service automatically starts monitoring when injected

    // ✅ App-level router spinner for lazy module loading
    // Provides immediate feedback during module download and resolver execution
    // Coordinates with module-level spinners via source tracking
    this.setupRouterSpinner();

    if (this.swUpdate.isEnabled) {
      // Check for updates every 5 minutes (more frequent than hourly)
      interval(5 * 60 * 1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.swUpdate.checkForUpdate();
        });

      // Check for updates immediately on app startup
      this.swUpdate.checkForUpdate().then(() => {
        console.log('[PWA] Initial update check completed');
      });

      // Check for updates when app becomes visible (user switches back to tab)
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.swUpdate.isEnabled) {
          console.log('[PWA] App became visible, checking for updates...');
          this.swUpdate.checkForUpdate();
        }
      });

      // Handle version updates
      this.swUpdate.versionUpdates
        .pipe(
          filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
          takeUntil(this.destroy$)
        )
        .subscribe((event) => {
          console.log(`[PWA] New version available: ${event.latestVersion.hash}`);

          const isIosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
          if (window.matchMedia('(display-mode: browser)').matches && !isIosStandalone) {
            return;
          }

          // Show update notification with auto-dismiss after 30 seconds
          const snackBarRef = this.snackBar.open(
            this.transloco.translate('core.updates.available'),
            this.transloco.translate('core.updates.apply'),
            { duration: 30000 }
          );

          // Helper: only reload when the salesrep order state is clean and no save is in flight.
          // A reload here would discard typed order state held only in volatile Akita memory.
          // Use synchronous facade getters to avoid silent rejection paths in async subscribe callbacks.
          const orderInProgress = (): boolean => this.orderUI.isDirty() || this.orderUI.isSaving();
          // Reload bypasses the route guard and loses the manual draft's files and retry identity.
          const manualEntryInProgress = (): boolean => this.router.url.startsWith('/app/order-inbox/manual');
          // A reload on the SMS screens discards the code the user is entering; the
          // update applies on the next full page load instead, same as for a dirty order.
          const loginInProgress = (): boolean => this.router.url.startsWith('/login/sms');
          const canReloadNow = (): boolean => !orderInProgress() && !manualEntryInProgress() && !loginInProgress();
          const suppressedReason = (): string =>
            loginInProgress() ? 'phone login in progress' : 'order entry is in progress';

          // Auto-reload after 5 seconds if user doesn't click and order state is safe.
          const autoReloadTimer = timer(5000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              if (canReloadNow()) {
                console.log('[PWA] Auto-reloading to apply new version');
                this.reloadPage();
              } else {
                console.log(`[PWA] Auto-reload suppressed — ${suppressedReason()}`);
              }
            });

          // Handle manual update action — also gated, so a tap during save does not destroy the order.
          snackBarRef
            .onAction()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              autoReloadTimer.unsubscribe();
              if (canReloadNow()) {
                console.log('[PWA] User triggered reload');
                this.reloadPage();
              } else {
                console.log(`[PWA] Manual reload suppressed — ${suppressedReason()}`);
                this.snackBar.open(
                  this.transloco.translate(
                    loginInProgress() ? 'core.updates.finishLoginFirst' : 'core.updates.saveOrderFirst'
                  ),
                  this.transloco.translate('shared.dialogs.ok'),
                  { duration: 4000 }
                );
              }
            });

          // Clear timer if snackbar is dismissed
          snackBarRef
            .afterDismissed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              autoReloadTimer.unsubscribe();
            });
        });

      // Handle unrecoverable errors
      this.swUpdate.unrecoverable.pipe(takeUntil(this.destroy$)).subscribe((event) => {
        console.error('[PWA] Unrecoverable error:', event.reason);
        this.snackBar
          .open(
            this.transloco.translate('core.updates.unrecoverable'),
            this.transloco.translate('core.updates.reload'),
            { duration: 0 }
          )
          .onAction()
          .subscribe(() => {
            this.reloadPage();
          });
      });
    }
  }

  /** Thin wrapper around location.reload so the update handlers are unit-testable. */
  private reloadPage(): void {
    window.location.reload();
  }

  /**
   * ✅ App-level router spinner management
   * Shows spinner AFTER guards pass (to avoid blocking canDeactivate dialogs)
   * Covers module loading and resolver execution
   * Hides on navigation complete, but only if no module-level spinner has taken over
   */
  private setupRouterSpinner(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      // ✅ Show spinner AFTER guards pass (GuardsCheckEnd)
      // This prevents blocking canDeactivate/canActivate dialogs
      if (event instanceof GuardsCheckEnd) {
        // Only show if guards passed (user confirmed or no guards blocked)
        if (event.shouldActivate) {
          // Suppress the router spinner inside the auth shell. Intra-auth
          // navigations (email↔sms, sms→otp, bare-/login redirect) reuse the
          // same AuthShell, run no resolvers, and the screens already drive
          // their own submit-time loading state — the global overlay only adds
          // a synthetic flicker on a route swap that needs nothing.
          if (event.url.startsWith('/login')) {
            console.log(`⏭️  [AppRouter] Skipping spinner for intra-auth nav: ${event.url}`);
          } else {
            // Clear any pending hide timeout
            if (this.navigationTimeout) {
              this.navigationTimeout.unsubscribe();
            }

            // Show spinner with context-aware message
            const message = this.getMessageForRoute(event.url);
            this.globalLoading.show(message, 'app-router');

            console.log(`🔄 [AppRouter] Guards passed, navigation to: ${event.url}`);
          }
        } else {
          // Guards blocked navigation (user clicked "No" in canDeactivate)
          console.log(`🚫 [AppRouter] Guards blocked navigation to: ${event.url}`);
        }
      }

      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        console.log(`✅ [AppRouter] Navigation ${event instanceof NavigationEnd ? 'completed' : 'cancelled/error'}`);

        // Small delay to allow module-level spinners to take over
        this.navigationTimeout = timer(150)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            const currentState = this.globalLoading.getState();

            // Only hide if:
            // 1. We're the ones showing it (source is 'app-router'), OR
            // 2. Source is 'unknown' (safety fallback)
            // If module-level spinner (e.g., 'salesrep-module') took over, don't hide
            if (currentState.source === 'app-router' || currentState.source === 'unknown') {
              console.log(`🔽 [AppRouter] Hiding spinner (source was: ${currentState.source})`);
              this.globalLoading.hide();
            } else {
              console.log(`⏭️  [AppRouter] Module-level spinner active (source: ${currentState.source}), not hiding`);
            }
          });
      }

      // Handle navigation errors
      if (event instanceof NavigationError) {
        console.error('❌ [AppRouter] Navigation error:', event.error);
        // Force hide on error after showing error message
        this.globalLoading.updateMessage(this.transloco.translate('core.loading.error'));
        timer(2000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.globalLoading.hide();
          });
      }
    });
  }

  /**
   * Get user-friendly loading message based on destination route
   */
  private getMessageForRoute(url: string): string {
    return this.transloco.translate(loadingMessageKeyForRoute(url));
  }
}
